<!-- The editor takes the viewport below the navbar: panels scroll on their own, the
     canvas gets the height. On a phone a summary stands in for the full editor. -->
<div
  class="flex flex-col gap-3 px-4 py-3"
  [class.lg:h-[calc(100dvh-5rem)]]="!loading() && !notFound() && !loadError()">
  @if (loading()) {
    <div class="space-y-4" aria-busy="true">
      <hlm-skeleton class="h-10 w-full" />
      <div class="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)_18rem]">
        <hlm-skeleton class="hidden h-96 lg:block" />
        <hlm-skeleton class="aspect-[1/1.414] max-h-[70vh] w-full" />
        <hlm-skeleton class="hidden h-96 lg:block" />
      </div>
    </div>
  } @else if (notFound()) {
    <div hlmAlert variant="destructive">
      <ng-icon name="lucideTriangleAlert" />
      <p hlmAlertTitle>Design nicht gefunden</p>
      <p hlmAlertDescription>
        <a class="underline" routerLink="/settings" [queryParams]="{ tab: 'voucher-design' }"
          >Zurück zu den Einstellungen</a
        >
      </p>
    </div>
  } @else if (loadError(); as message) {
    <div hlmAlert variant="destructive">
      <ng-icon name="lucideTriangleAlert" />
      <p hlmAlertTitle>{{ message }}</p>
      <p hlmAlertDescription>
        <button hlmBtn variant="link" size="sm" type="button" (click)="load()">
          Erneut versuchen
        </button>
      </p>
    </div>
  } @else if (compact() && !forceFull() && store.design(); as design) {
    <app-mobile-summary
      [design]="design"
      [previewing]="downloadingPdf()"
      (preview)="downloadPreviewPdf()"
      (activate)="askActivate()"
      (edit)="forceFull.set(true)" />
  } @else {
    <app-editor-toolbar
      #toolbar
      [name]="store.design()?.name ?? ''"
      [voucherType]="store.voucherType()"
      [busy]="saving() || scaling() || copying() || changingBackground()"
      [dirty]="store.dirty()"
      [saving]="saving()"
      [previewing]="previewing()"
      [canSave]="store.canSave()"
      [canActivate]="store.canActivate()"
      [missingBranding]="store.missingBranding()"
      [isActive]="store.design()?.is_active ?? false"
      [errors]="allErrors()"
      [warnings]="store.validation().warnings"
      (focusElement)="focusElement($event)"
      [zoom]="store.zoom()"
      [longTexts]="store.longTexts()"
      [canUndo]="store.canUndo()"
      [canRedo]="store.canRedo()"
      [restoring]="store.restoring()"
      [showOutlines]="store.showOutlines()"
      [paletteOpen]="store.paletteOpen()"
      [inspectorOpen]="store.inspectorOpen()"
      (undo)="store.undo()"
      (redo)="store.redo()"
      (outlinesChange)="store.showOutlines.set($event)"
      (paletteToggle)="store.paletteOpen.set(!store.paletteOpen())"
      (inspectorToggle)="store.inspectorOpen.set(!store.inspectorOpen())"
      (shortcuts)="shortcutsOpen.set(true)"
      (longTextsChange)="store.longTexts.set($event)"
      (realSize)="showRealSize()"
      (save)="save()"
      (preview)="preview()"
      (activate)="askActivate()"
      (copy)="copy($event)"
      (rename)="rename($event)"
      (zoomChange)="store.setZoom($event)"
      (fit)="store.fitToView()" />

    @if (actionError(); as message) {
      <div hlmAlert variant="destructive">
        <ng-icon name="lucideTriangleAlert" />
        <p hlmAlertDescription>{{ message }}</p>
      </div>
    }
    @if (store.design()?.warnings?.length) {
      <div hlmAlert>
        <ng-icon name="lucideTriangleAlert" />
        <p hlmAlertDescription>{{ store.design()!.warnings.join(' ') }}</p>
      </div>
    }

    <div
      class="grid min-h-0 flex-1 gap-3 lg:grid-cols-[var(--editor-columns)]"
      [style.--editor-columns]="gridColumns()">
      <div
        class="order-2 min-h-0 overflow-y-auto lg:order-1"
        [class.lg:hidden]="!store.paletteOpen()">
        <app-element-palette
          [catalog]="store.catalog()"
          [checklist]="store.checklist()"
          [elements]="store.elements()"
          [selectedIds]="store.selectedIds()"
          [voucherType]="store.voucherType()"
          [branding]="store.branding()"
          (add)="addElement($event)"
          (addText)="addText($event)"
          (addShape)="addShape($event)"
          (addRequired)="addRequired()"
          (pickImage)="openLibrary('add')"
          (select)="focusElement($event)"
          (move)="store.move($event.id, $event.delta)" />
      </div>
      <div class="order-1 flex min-h-[480px] min-w-0 flex-col lg:order-2 lg:min-h-0">
        <app-design-canvas
          class="min-h-0 flex-1"
          [previewSrc]="previewSrc()"
          [backgroundColor]="pageColor()"
          [page]="store.page()"
          [elements]="store.elements()"
          [selectedIds]="store.selectedIds()"
          [scale]="store.scale()"
          [catalogByKind]="store.catalogByKind()"
          [placeholders]="store.allowedPlaceholders()"
          [fontCss]="store.fontCss()"
          [imageSrcFor]="imageSrcFor()"
          [longTexts]="store.longTexts()"
          [issuesByElement]="store.issuesByElement()"
          [outlines]="store.showOutlines()"
          (select)="$event ? store.select($event.id, $event.toggle) : store.select(null)"
          (selectMany)="store.selectMany($event.ids, $event.add)"
          (drillIn)="store.drillIn($event)"
          [groupBox]="store.selectedGroup()?.bounds ?? null"
          [editingId]="store.editing()"
          [editSelectAll]="store.editSelectAll()"
          (editStart)="store.startEditing($event)"
          (editEnd)="store.stopEditing()"
          (contentChange)="store.patchElement($event.id, { content: $event.content })"
          (nudge)="store.nudge($event.dx, $event.dy)"
          (remove)="store.remove($event)"
          (elementChange)="onCanvasRects($event, false)"
          (elementCommit)="onCanvasRects($event, true)"
          (focusChange)="onCanvasFocus($event)"
          (fitScaleChange)="store.setFitScale($event.fit, $event.width)"
          (zoomBy)="store.setZoom(store.zoom() * $event)" />
      </div>
      <div class="order-3 min-h-0 overflow-y-auto" [class.lg:hidden]="!store.inspectorOpen()">
        @if (store.selectedIds().size > 1) {
          <app-selection-panel
            [count]="store.selectedIds().size"
            [isGroup]="store.selectionIsGroup()"
            [hasGroups]="selectionHasGroups()"
            [elements]="store.selectedElements()"
            [catalog]="store.catalog()"
            (patchScope)="patchSelection($event)"
            (duplicate)="duplicate()"
            (align)="store.align($event)"
            (distribute)="store.distribute($event)"
            (group)="store.group()"
            (ungroup)="store.ungroup()"
            (remove)="removeSelected()" />
        } @else if (store.selected()) {
          <app-element-inspector
            [element]="store.selected()"
            [entry]="store.selectedEntry()"
            [page]="store.page()"
            [catalog]="store.catalog()"
            [placeholders]="store.allowedPlaceholders()"
            [voucherType]="store.voucherType()"
            [index]="store.indexOf(store.selectedId() ?? '')"
            [count]="store.layerCount()"
            [picture]="selectedPicture()"
            (pickImage)="openLibrary('replace')"
            (alignToPage)="store.align($event)"
            (selectGroup)="store.selectGroupOf(store.selectedId() ?? '')"
            (historyStep)="$event === 'undo' ? store.undo() : store.redo()"
            (patch)="patchSelected($event)"
            (duplicate)="duplicate()"
            (remove)="removeSelected()"
            (moveBack)="moveSelected(-1)"
            (moveFront)="moveSelected(1)" />
        } @else if (store.design(); as design) {
          <app-page-panel
            [design]="design"
            [dirty]="store.dirty()"
            [busy]="scaling() || saving() || changingBackground()"
            (applyScale)="applyPrintScale($event)"
            (backgroundFile)="setBackgroundFile($event)"
            (pickTemplate)="openTemplateDialog()"
            (removeBackground)="removeBackground()"
            (orientation)="setOrientation($event)"
            (pageColor)="setPageColor($event)"
            (fileError)="actionError.set($event)" />
        }
      </div>
    </div>
  }
</div>

<app-shortcuts-dialog [open]="shortcutsOpen()" (close)="shortcutsOpen.set(false)" />

<app-image-library-dialog
  [open]="libraryMode() !== null"
  [usedIds]="usedImageIds()"
  [maxImages]="store.catalog()?.limits?.max_images ?? 30"
  (pick)="onPicturePicked($event)"
  (close)="libraryMode.set(null)" />

<app-background-template-dialog
  [open]="templateDialogOpen()"
  [busy]="changingBackground() || saving()"
  (choose)="setBackgroundTemplate($event)"
  (close)="templateDialogOpen.set(false)" />

<app-preview-dialog
  [open]="previewOpen()"
  [imageUrl]="previewImageUrl()"
  [loading]="previewing()"
  [downloading]="downloadingPdf()"
  [error]="previewError()"
  [productDesign]="store.voucherType() === 'product'"
  (close)="closePreview()"
  (download)="downloadPreviewPdf()"
  (retry)="loadPreviewImage()" />

<hlm-alert-dialog
  [state]="leaveDialogOpen() ? 'open' : 'closed'"
  (stateChanged)="onLeaveDialogState($event)">
  <hlm-alert-dialog-content *hlmAlertDialogPortal class="max-w-md">
    <div hlmAlertDialogHeader>
      <h3 hlmAlertDialogTitle>Ungespeicherte Änderungen verwerfen?</h3>
      <p hlmAlertDialogDescription>Das Layout wurde seit dem letzten Speichern verändert.</p>
    </div>
    <div hlmAlertDialogFooter>
      <button hlmAlertDialogCancel hlmBtn variant="outline" (click)="resolveLeave(false)">
        Weiter bearbeiten
      </button>
      <button hlmAlertDialogAction hlmBtn variant="destructive" (click)="resolveLeave(true)">
        Verwerfen
      </button>
    </div>
  </hlm-alert-dialog-content>
</hlm-alert-dialog>

<hlm-alert-dialog
  [state]="activateDialogOpen() ? 'open' : 'closed'"
  (stateChanged)="onActivateDialogState($event)">
  <hlm-alert-dialog-content *hlmAlertDialogPortal class="max-w-md">
    <div hlmAlertDialogHeader>
      <h3 hlmAlertDialogTitle>Design verwenden?</h3>
      <p hlmAlertDialogDescription>{{ activateText() }}</p>
    </div>
    <div hlmAlertDialogFooter>
      <button hlmAlertDialogCancel hlmBtn variant="outline" (click)="activateDialogOpen.set(false)">
        Abbrechen
      </button>
      <button hlmAlertDialogAction hlmBtn (click)="confirmActivate()">Verwenden</button>
    </div>
  </hlm-alert-dialog-content>
</hlm-alert-dialog>
